import { useState, useEffect, useRef, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { X, AlertCircle, Keyboard } from "lucide-react";

export const QRScanner = ({ onScan, onClose }) => {
  const [error, setError] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const scannerRef = useRef(null);
  const isRunningRef = useRef(false);
  const hasScannedRef = useRef(false);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current && isRunningRef.current) {
      isRunningRef.current = false;
      try {
        await scannerRef.current.stop();
      } catch (e) {
        // ignore
      }
      try {
        scannerRef.current.clear();
      } catch (e) {
        // ignore
      }
      scannerRef.current = null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const startScanner = async () => {
      try {
        const scanner = new Html5Qrcode("qr-reader-container");
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (!hasScannedRef.current && mounted) {
              hasScannedRef.current = true;
              stopScanner().then(() => {
                onScan(decodedText);
              });
            }
          },
          () => {}
        );
        if (mounted) isRunningRef.current = true;
      } catch (err) {
        if (mounted) {
          setError("Camera not available. Use manual entry below.");
          setShowManual(true);
        }
      }
    };

    startScanner();

    return () => {
      mounted = false;
      stopScanner();
    };
  }, [onScan, stopScanner]);

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  const handleManualSubmit = async () => {
    if (manualInput.trim()) {
      await stopScanner();
      onScan(manualInput.trim());
    }
  };

  return (
    <div className="scanner-overlay" data-testid="qr-scanner-modal">
      <div className="relative w-full max-w-md mx-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          data-testid="close-scanner-btn"
          className="absolute -top-12 right-0 text-white hover:bg-white/20 rounded-full z-50"
        >
          <X className="w-6 h-6" strokeWidth={2} />
        </Button>

        <div className="bg-stone-900 rounded-3xl overflow-hidden shadow-2xl">
          {/* Camera header */}
          <div className="p-4 text-center bg-stone-900">
            <h3 className="font-bold text-lg text-white">Scan QR Code</h3>
            <p className="text-sm text-stone-400">Position the QR code within the frame</p>
          </div>

          {error ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <p className="text-white text-sm">{error}</p>
            </div>
          ) : (
            <div id="qr-reader-container" style={{ width: "100%", minHeight: "300px" }} />
          )}

          {/* Manual fallback */}
          <div className="p-4 space-y-3 border-t border-stone-700">
            {!showManual ? (
              <Button
                variant="ghost"
                onClick={() => setShowManual(true)}
                data-testid="show-manual-input-btn"
                className="w-full text-stone-400 hover:text-white hover:bg-stone-800 gap-2"
              >
                <Keyboard className="w-4 h-4" />
                Enter QR data manually
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-stone-400 text-center">Paste QR code data:</p>
                <Input
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
                  placeholder="Paste QR code content..."
                  className="bg-stone-800 text-white placeholder-stone-500 border-stone-600"
                  data-testid="manual-qr-input"
                />
                <Button
                  onClick={handleManualSubmit}
                  data-testid="manual-submit-btn"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                >
                  Submit
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
